import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, Spin, message } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import './BackgroundCheckResult.css';

// 限制高消费/失信案件的 sign 字段：为空(''/null/undefined)表示“正在执行中(上架)”，排在前面并计入异常；
// 非空(如 sign='1' 表示已下架/已终本)为历史案件，排在后面
const isActiveSign = (sign) => sign === '' || sign === null || sign === undefined;

// 统计某类下“异常(正在执行中)”的案件条数
const countAbnormal = (section, type) => {
  const cases = section?.cases || [];
  if (type === 'enforcement') {
    return cases.filter(c => c.statute === '执行中').length;
  }
  return cases.filter(c => isActiveSign(c.sign)).length;
};

// 案件排序：正在执行中的按立案日期(regdate)倒排在前，已下架/已终本的按日期倒排在后
const sortCases = (cases, type) => {
  if (!cases || cases.length === 0) return [];
  const arr = [...cases];
  const byDateDesc = (a, b) => String(b.regdate || '').localeCompare(String(a.regdate || ''));
  if (type === 'enforcement') {
    // 被执行人案件无立案日期字段，仅按状态分组：执行中在前，其余在后
    const active = arr.filter(c => c.statute === '执行中');
    const rest = arr.filter(c => c.statute !== '执行中');
    return [...active, ...rest];
  }
  const active = arr.filter(c => isActiveSign(c.sign));
  const history = arr.filter(c => !isActiveSign(c.sign));
  active.sort(byDateDesc);
  history.sort(byDateDesc);
  return [...active, ...history];
};

export default function BackgroundCheckResult({ id, checkId, navigateTo }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // 各类案件默认折叠
  const [expanded, setExpanded] = useState({ limit: false, dishonesty: false, enforcement: false });

  useEffect(() => {
    loadCheckResult();
  }, [id, checkId]);

  const loadCheckResult = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = checkId
        ? `/api/housekeepers/${id}/background-check/${checkId}`
        : `/api/housekeepers/${id}/background-check/latest`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.code === 200 && res.data.data) {
        setResult(res.data.data);
        setExpanded({ limit: false, dishonesty: false, enforcement: false });
      } else {
        message.error(res.data.message || '加载失败');
      }
    } catch (error) {
      console.error('加载背调结果失败', error);
      message.error('加载失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#52c41a';
      case 'abnormal': return '#ff4d4f';
      case 'history': return '#faad14';
      default: return '#d9d9d9';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'normal': return '正常';
      case 'abnormal': return '异常';
      case 'history': return '历史';
      default: return '未知';
    }
  };

  // 标签文案：异常时显示“异常X条”
  const getTagText = (section, type) => {
    const status = section?.status;
    if (status === 'abnormal') {
      const n = countAbnormal(section, type);
      return n > 0 ? `异常${n}条` : '异常';
    }
    return getStatusText(status);
  };

  const getOverallStatusText = (status) => {
    switch (status) {
      case 'normal': return '信用正常';
      case 'abnormal': return '信用异常';
      case 'history': return '有异常历史';
      default: return '未知';
    }
  };

  const getOverallStatusDesc = (status) => {
    switch (status) {
      case 'normal': return '该阿姨信用状况良好';
      case 'abnormal': return '该阿姨存在信用问题';
      case 'history': return '该阿姨有历史异常记录';
      default: return '';
    }
  };

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const handleRequery = () => {
    navigateTo('background-check', { id });
  };

  // 渲染单个案件
  const renderCaseItem = (type, item, index) => {
    if (type === 'enforcement') {
      return (
        <div key={index} className="bcr-case-item">
          <div className="bcr-case-info">
            <div>案号：{item.casenum}</div>
            {item.money && <div>执行标的：{item.money}</div>}
            {item.statute && <div>案件状态：{item.statute}</div>}
          </div>
        </div>
      );
    }
    if (type === 'dishonesty') {
      return (
        <div key={index} className="bcr-case-item">
          <div className="bcr-case-index">{index + 1}.</div>
          <div className="bcr-case-info">
            <div>案号：{item.casecode}</div>
            {item.courtname && <div>执行法院：{item.courtname}</div>}
            {item.areaname && <div>省份：{item.areaname}</div>}
            {item.gistcid && <div>执行依据文号：{item.gistcid}</div>}
            {item.regdate && <div>立案时间：{item.regdate}</div>}
            {item.signalDesc && <div>案件状态：{item.signalDesc}</div>}
          </div>
        </div>
      );
    }
    // limit（限制高消费）
    return (
      <div key={index} className="bcr-case-item">
        <div className="bcr-case-index">{index + 1}.</div>
        <div className="bcr-case-info">
          <div>案号：{item.casecode}</div>
          {item.courtname && <div>法院：{item.courtname}</div>}
          {item.regdate && <div>立案日期：{item.regdate}</div>}
          {item.signalDesc && <div>案件状态：{item.signalDesc}</div>}
        </div>
      </div>
    );
  };

  // 折叠状态下的 “>> 查看详情” 展开链接：abnormal 时内联在警示语后(inline)，其余状态独立成行(standalone)
  const renderExpandLink = (section, key, standalone) => {
    const cases = section?.cases || [];
    if (cases.length === 0 || expanded[key]) return null;
    return (
      <span
        className={`bcr-toggle-link${standalone ? ' bcr-toggle-standalone' : ''}`}
        onClick={() => toggle(key)}
      >
        {'>> 查看详情'}
      </span>
    );
  };

  // 展开状态下的案件列表 + 末尾 “<<” 收起链接
  const renderCases = (type, section, key) => {
    const cases = section?.cases || [];
    if (cases.length === 0 || !expanded[key]) return null;
    return (
      <>
        <div className="bcr-case-list">
          {sortCases(cases, type).map((item, index) => renderCaseItem(type, item, index))}
        </div>
        <div className="bcr-toggle-row">
          <span className="bcr-toggle-link" onClick={() => toggle(key)}>{'<<'} 收起</span>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="bcr-loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="bcr-empty-container">
        <Card>
          <div className="bcr-empty-content">
            <p>暂无背调记录</p>
            <Button type="primary" onClick={() => navigateTo('background-check', { id })}>
              去查询
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="bcr-container">
      <Card
        title={
          <div className="bcr-card-header">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigateTo('detail', { id })}>返回</Button>
            <span className="bcr-card-title">背调结果</span>
          </div>
        }
      >
        {/* 总体状态 */}
        <div className="bcr-overall-status" style={{ backgroundColor: getStatusColor(result.overall_status) }}>
          <div className="bcr-status-icon">
            {result.overall_status === 'normal' && <CheckCircleOutlined />}
            {result.overall_status === 'abnormal' && <CloseCircleOutlined />}
            {result.overall_status === 'history' && <WarningOutlined />}
          </div>
          <div className="bcr-status-info">
            <div className="bcr-status-title">{getOverallStatusText(result.overall_status)}</div>
            <div className="bcr-status-desc">{getOverallStatusDesc(result.overall_status)}</div>
          </div>
        </div>

        {/* 身份证验证 */}
        <Card className="bcr-section-card" title="身份证实名认证" extra={
          <Tag color={result.idcard_check?.result === 0 ? 'success' : 'error'}>
            {result.idcard_check?.result === 0 ? '一致' : result.idcard_check?.result === 2 ? '无效' : '不一致'}
          </Tag>
        }>
          <div className="bcr-info-content">
            <div className="bcr-info-row">
              <span className="bcr-info-label">验证结果：</span>
              <span className="bcr-info-value">{result.idcard_check?.desc}</span>
            </div>
            {result.idcard_check?.result === 0 && (
              <>
                <div className="bcr-info-row">
                  <span className="bcr-info-label">性别：</span>
                  <span className="bcr-info-value">{result.idcard_check?.sex}</span>
                </div>
                <div className="bcr-info-row">
                  <span className="bcr-info-label">出生日期：</span>
                  <span className="bcr-info-value">{result.idcard_check?.birthday}</span>
                </div>
                <div className="bcr-info-row">
                  <span className="bcr-info-label">地址：</span>
                  <span className="bcr-info-value">{result.idcard_check?.address}</span>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* 限制高消费 */}
        <Card className="bcr-section-card" title={`限制高消费 ${result.limit_consumption?.case_count > 0 ? `(${result.limit_consumption.case_count}条)` : ''}`} extra={
          <Tag color={getStatusColor(result.limit_consumption?.status)}>
            {getTagText(result.limit_consumption, 'limit')}
          </Tag>
        }>
          <div className="bcr-section-content">
            {result.limit_consumption?.status === 'normal' && (
              <div className="bcr-tip-text">
                数据来自国家信息中心官网，限制消费人员：通过法院限制消费公开信息核实候选人是否有法院限制消费记录，数据仅供参考，如有疑义，请反馈复核，最终以国家公示平台结果为准。
              </div>
            )}
            {result.limit_consumption?.status === 'abnormal' && (
              <div className="bcr-warning-text">
                信用异常，该用户是限制高消费人员
                {renderExpandLink(result.limit_consumption, 'limit', false)}
              </div>
            )}
            {result.limit_consumption?.status !== 'abnormal' && renderExpandLink(result.limit_consumption, 'limit', true)}
            {renderCases('limit', result.limit_consumption, 'limit')}
            <div className="bcr-query-time">查询时间：{result.query_time}</div>
          </div>
        </Card>

        {/* 失信被执行人 */}
        <Card className="bcr-section-card" title={`老赖（失信）被执行人 ${result.dishonesty?.case_count > 0 ? `(${result.dishonesty.case_count}条)` : ''}`} extra={
          <Tag color={getStatusColor(result.dishonesty?.status)}>
            {getTagText(result.dishonesty, 'dishonesty')}
          </Tag>
        }>
          <div className="bcr-section-content">
            {result.dishonesty?.status === 'normal' && (
              <div className="bcr-tip-text">
                数据来自国家信息中心官网，老赖(失信被执行人)是指被人民法院判决需承担责任，但恶意不执行判决的人。查询结果中，如有[上架]字样则代表该案件在公示中，如有[下架]字样代表案件已执行完毕不再公示。如有疑义，请反馈复核，最终以国家公示平台结果为准。
              </div>
            )}
            {result.dishonesty?.status === 'abnormal' && (
              <div className="bcr-warning-text">
                信用异常，该用户是法院被执行人
                {renderExpandLink(result.dishonesty, 'dishonesty', false)}
              </div>
            )}
            {result.dishonesty?.status !== 'abnormal' && renderExpandLink(result.dishonesty, 'dishonesty', true)}
            {renderCases('dishonesty', result.dishonesty, 'dishonesty')}
            <div className="bcr-query-time">查询时间：{result.query_time}</div>
          </div>
        </Card>

        {/* 被执行人 */}
        <Card className="bcr-section-card" title={`法院被执行人 ${result.enforcement?.case_count > 0 ? `(${result.enforcement.case_count}条)` : ''}`} extra={
          <Tag color={getStatusColor(result.enforcement?.status)}>
            {getTagText(result.enforcement, 'enforcement')}
          </Tag>
        }>
          <div className="bcr-section-content">
            {result.enforcement?.status === 'normal' && (
              <div className="bcr-tip-text">
                数据来自国家信息中心官网，通过法院执行公开信息核实候选人是否有法院执行记录，数据仅供参考，如有疑义，请反馈复核，最终以国家公示平台结果为准。
              </div>
            )}
            {result.enforcement?.status === 'abnormal' && (
              <div className="bcr-warning-text">
                异常，该用户是法院被执行人
                {renderExpandLink(result.enforcement, 'enforcement', false)}
              </div>
            )}
            {result.enforcement?.status !== 'abnormal' && renderExpandLink(result.enforcement, 'enforcement', true)}
            {renderCases('enforcement', result.enforcement, 'enforcement')}
            <div className="bcr-query-time">查询时间：{result.query_time}</div>
          </div>
        </Card>

        {/* 操作按钮 */}
        <div className="bcr-action-buttons">
          <Button onClick={() => navigateTo('detail', { id })}>返回详情</Button>
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleRequery}>
            重新查询
          </Button>
        </div>
      </Card>
    </div>
  );
}
